import { useEffect, useRef, useState, type ReactNode } from 'react'
import { MonitorSmartphone } from 'lucide-react'

import { Button } from '@/components/ui/button'

// Register-window grace: register başarıyla current'ı yazdıktan SONRA, register'dan
// ÖNCE gönderilip GEÇ dönen provider query'leri hâlâ 401-superseded taşıyabilir
// (current=eski iken yola çıkmışlardı). Bu tail yanlış-pozitifi, register'dan sonraki
// bu kısa pencerede superseded sinyalini yok sayarak kapatırız. Gerçek bir superseded
// olsaydı pencere sonrası bir sonraki istek yine tetiklerdi.
const REGISTER_GRACE_MS = 3000

const SUPERSEDED_FLAG = 'session:superseded'

/** sessionStorage superseded flag'ini güvenle temizler (erişilemezse yoksayar). */
function clearSupersededFlag() {
  try {
    window.sessionStorage.removeItem(SUPERSEDED_FLAG)
  } catch {
    /* sessionStorage erişilemez — yoksay */
  }
}

/**
 * Tek-session enforcement provider (Phase 2) — SADECE SE-02 (superseded görünürlük).
 *
 * `session:superseded` sinyalinde (api-client, 401 + X-Session-Superseded) KALICI
 * logout ekranı gösterir. Bu ekran refresh/retry SUNMAZ — normal-401 refresh
 * akışından ayrıdır; tek çıkış "Tekrar giriş yap" (full reload + flag temizliği).
 *
 * NOT: `registerSession` (SE-01 current-session yazımı) BU provider'da DEĞİL —
 * `login.tsx` içinde navigasyondan ÖNCE çağrılır. Sebep: Supabase `onAuthStateChange`
 * sayfa reload'ında `SIGNED_IN`'i tutarsız fırlatır; register'ı buna bağlamak
 * reload'da oturumu yeniden sahiplenip last-login-wins'i bozardı (superseded olması
 * gereken cihaz kendini geri register eder). Register'ı yalnız gerçek login
 * aksiyonuna bağlamak bu belirsizliği ortadan kaldırır.
 */
export function SessionEnforcementProvider({ children }: { children: ReactNode }) {
  // Cold-load race önleme: mount anında sessionStorage flag'ini oku. api-client
  // superseded-401'de flag yazar; tam reload'da window event kaçsa bile ekran
  // burada deterministik gösterilir. Flag yalnız gerçek login (login.tsx) veya
  // "Tekrar giriş yap" ile temizlenir → reload superseded'i sıfırlamaz.
  const [superseded, setSuperseded] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem(SUPERSEDED_FLAG) === '1',
  )
  // Son başarılı register zamanı (grace penceresi için). Ref — re-render tetiklemez.
  // KRİTİK: -Infinity ile başlat. performance.now() sayfa yüklenince ~0'dan başlar;
  // 0 ile başlatılsaydı her reload'ın ilk 3s'inde grace YANLIŞLIKLA aktif olur ve
  // A'nın reload'daki GERÇEK superseded'ini yutardı (→ /login'e düşme bug'ı). -Infinity
  // ile grace ancak gerçek bir register sonrası aktifleşir.
  const lastRegisteredAt = useRef(Number.NEGATIVE_INFINITY)

  // SE-02: api-client'ten gelen kalıcı logout sinyalini dinle (app mount'luyken
  // gelen canlı superseded-401 için; cold-load'da flag zaten mount'ta okundu).
  useEffect(() => {
    const handler = () => {
      // Register-window grace: register'dan hemen sonraki tail 401-superseded'i
      // yok say (register'dan önce yola çıkmış geç dönen istek). Gerçekse sonraki
      // istek yine tetikler. performance.now() monotonik → saat kaymasına dayanıklı.
      if (performance.now() - lastRegisteredAt.current < REGISTER_GRACE_MS) {
        clearSupersededFlag()
        return
      }
      setSuperseded(true)
    }
    window.addEventListener('session:superseded', handler)

    // Cold-load event-miss güvencesi: tam sayfa reload'ında superseded-401,
    // provider listener'ı BAĞLANMADAN gelirse event kaçar (fire-and-forget) ve
    // kullanıcı /login'e düşer. api-client flag'i yazdığı için, mount'tan sonra
    // flag'i kısa süre YOKLA — set olmuşsa ekranı deterministik göster. Grace
    // penceresindeyse yoksay (login yanlış-pozitifi). Ekran görününce polling durur.
    let polls = 0
    const pollId = window.setInterval(() => {
      polls += 1
      if (window.sessionStorage.getItem(SUPERSEDED_FLAG) === '1') {
        if (performance.now() - lastRegisteredAt.current >= REGISTER_GRACE_MS) {
          setSuperseded(true)
        }
        window.clearInterval(pollId)
      } else if (polls >= 12) {
        window.clearInterval(pollId) // ~6s (12×500ms) sonra bırak
      }
    }, 500)

    return () => {
      window.removeEventListener('session:superseded', handler)
      window.clearInterval(pollId)
    }
  }, [])

  // Register-penceresi yanlış-pozitifini geri al: gerçek login'de (login.tsx /
  // auth-callback) register başarıyla current'ı BU oturuma yazınca session-api
  // 'session:registered' yayar. Login sırasında paralel provider query'lerinin
  // register'dan önce aldığı 401-superseded'i temizle → yeni giren kesintisiz devam.
  // Reload register ETMEZ → bu event reload'da gelmez → gerçek superseded korunur.
  useEffect(() => {
    const handler = () => {
      lastRegisteredAt.current = performance.now()
      clearSupersededFlag()
      setSuperseded(false)
    }
    window.addEventListener('session:registered', handler)
    return () => window.removeEventListener('session:registered', handler)
  }, [])

  if (superseded) {
    return <SessionSupersededScreen />
  }

  return <>{children}</>
}

/**
 * Kalıcı "başka yerde giriş yapıldı" ekranı.
 * Bilinçli olarak refresh/retry SUNMAZ — oturum geri gelmeyecek; tek yol yeniden giriş.
 */
function SessionSupersededScreen() {
  const handleRelogin = () => {
    // Superseded flag'ini temizle — yoksa /login'de ekran tekrar açılır (provider
    // login sayfasını da sarıyor). Sonra full reload: in-memory state + query cache temiz.
    clearSupersededFlag()
    window.location.href = '/login'
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-background/95 p-6 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-xl">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-amber-500/20 bg-amber-500/10">
          <MonitorSmartphone className="h-7 w-7 text-amber-400" />
        </div>
        <h2 className="text-lg font-semibold text-foreground">Oturum Sonlandırıldı</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Hesabınıza başka bir cihazdan giriş yapıldı. Güvenliğiniz için bu oturum sonlandırıldı.
        </p>
        <Button className="mt-6 w-full" onClick={handleRelogin}>
          Tekrar giriş yap
        </Button>
      </div>
    </div>
  )
}
